import { useCallback, useState } from "react";
import appLogger from "../../../infra/logger/appLogger";
import { useAppDispatch, useAppSelector } from "../../hook";
import { useTranslation } from "../../localization";
import { RateLimitFlows, useRateLimitErrors } from "../../miscellaneous";
import { useUserSettings } from "../../user";
import { createCharacterTranslation, getCharacterTranslation } from "../characterSlice";
import { CharacterFlow, CharacterTranslationTaskStatus } from "../types";
import useCharacter from "../useCharacter";
import { ANALYZE_CONTENT_THRESHOLD, CharacterTranslationStatus, FINALIZE_TRANSLATION_THRESHOLD, MAX_PROGRESS, MAX_PROGRESS_WITH_BUFFER, POLL_STATUS_INTERVAL_TIME, PROGRESS_INTERVAL_TIME, SUCCESS_CALLBACK_DELAY, TOTAL_TRANSLATION_DURATION, TRANSLATE_GREETING_THRESHOLD, TRANSLATE_PERSONA_THRESHOLD, TRANSLATE_TITLE_THRESHOLD, } from "./constants";
import getCharacterTranslationStatusMessage from "./getCharacterTranslationStatusMessage";
const characterTranslationLogger = appLogger.withTag("CharacterTranslation");
export default function useCharacterTranslation() {
    const { getCharacterData: refreshCharacter } = useCharacter();
    const { getMessage: getRateLimitErrorMessage } = useRateLimitErrors();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { userChatLanguage } = useUserSettings();
    const { loading, loadingType } = useAppSelector((state) => state.character);
    const [translationStatus, setTranslationStatus] = useState("");
    const [translationError, setTranslationError] = useState(null);
    const [translationProgress, setTranslationProgress] = useState(0);
    const [isTranslating, setIsTranslating] = useState(false);
    const translationStatusMessages = getCharacterTranslationStatusMessage(t);
    const localizedTranslationStatus = translationStatus
        ? (translationStatusMessages[translationStatus] ?? "")
        : "";
    const resetError = () => {
        setTranslationError(null);
    };
    const resetStatus = () => {
        setTranslationStatus("");
    };
    const getTranslation = useCallback(async (params) => {
        try {
            setTranslationError(null);
            return await dispatch(getCharacterTranslation(params)).unwrap();
        }
        catch (error) {
            const apiError = error;
            const errorMessage = apiError?.message || t("error:fallbackError");
            setTranslationError({
                errorType: CharacterFlow.GET_TRANSLATION,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            characterTranslationLogger.error(`Error getting translation: ${errorMessage}`, apiError);
            throw error;
        }
    }, [dispatch, t]);
    const createTranslation = useCallback(async (params) => {
        try {
            setTranslationError(null);
            return await dispatch(createCharacterTranslation(params)).unwrap();
        }
        catch (error) {
            const apiError = error;
            const errorMessage = getRateLimitErrorMessage({
                errorCode: apiError?.errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: RateLimitFlows.TRANSLATE_CHATBOT,
                },
            }) ||
                apiError.message ||
                t("error:fallbackError");
            setTranslationError({
                errorType: CharacterFlow.CREATE_TRANSLATION,
                errorMessage: errorMessage,
                errorCode: apiError?.errorCode,
            });
            characterTranslationLogger.error(`Error creating character translation: ${errorMessage}`, apiError);
            throw { ...apiError, message: errorMessage };
        }
    }, [dispatch, getRateLimitErrorMessage, t]);
    const onUserStartTranslation = useCallback(async (characterId, options) => {
        const { onSuccess, onError } = options || {};
        setIsTranslating(true);
        setTranslationProgress(0);
        setTranslationStatus(CharacterTranslationStatus.PREPARE_TRANSLATION);
        setTranslationError(null);
        let progressInterval = null;
        let statusInterval = null;
        const startTime = Date.now();
        const totalDuration = TOTAL_TRANSLATION_DURATION; // 45 seconds in milliseconds
        const updateProgress = () => {
            const elapsedTime = Date.now() - startTime;
            const calculatedProgress = Math.min((elapsedTime / totalDuration) * MAX_PROGRESS, MAX_PROGRESS_WITH_BUFFER); // Cap at 98%
            setTranslationProgress(Math.floor(calculatedProgress));
            // Update status message based on progress percentage
            if (calculatedProgress < ANALYZE_CONTENT_THRESHOLD) {
                setTranslationStatus(CharacterTranslationStatus.ANALYZE_CONTENT);
            }
            else if (calculatedProgress < TRANSLATE_TITLE_THRESHOLD) {
                setTranslationStatus(CharacterTranslationStatus.TRANSLATE_TITLE);
            }
            else if (calculatedProgress < TRANSLATE_PERSONA_THRESHOLD) {
                setTranslationStatus(CharacterTranslationStatus.TRANSLATE_PERSONA);
            }
            else if (calculatedProgress < TRANSLATE_GREETING_THRESHOLD) {
                setTranslationStatus(CharacterTranslationStatus.TRANSLATE_GREETING);
            }
            else if (calculatedProgress < FINALIZE_TRANSLATION_THRESHOLD) {
                setTranslationStatus(CharacterTranslationStatus.FINALIZE_TRANSLATION);
            }
            else {
                setTranslationStatus(CharacterTranslationStatus.WAITING_FOR_CONFIRMATION);
            }
        };
        // Start progress update interval
        progressInterval = setInterval(updateProgress, PROGRESS_INTERVAL_TIME);
        try {
            // Create the translation task
            await createTranslation({
                character_id: characterId,
                target_language: userChatLanguage,
            });
            // Start polling for status
            statusInterval = setInterval(async () => {
                try {
                    const result = await getTranslation({
                        character_id: characterId,
                        target_language: userChatLanguage,
                    });
                    if (result.status === CharacterTranslationTaskStatus.COMPLETED) {
                        // Clear both intervals
                        if (statusInterval)
                            clearInterval(statusInterval);
                        if (progressInterval)
                            clearInterval(progressInterval);
                        // Set progress to 100% when complete
                        setTranslationProgress(MAX_PROGRESS);
                        setTranslationStatus(CharacterTranslationStatus.COMPLETED);
                        // Small delay before calling success callback
                        setTimeout(() => {
                            setIsTranslating(false);
                            refreshCharacter(characterId, { language: userChatLanguage });
                            onSuccess?.();
                        }, SUCCESS_CALLBACK_DELAY);
                    }
                    else if (result.status === CharacterTranslationTaskStatus.FAILED) {
                        if (statusInterval)
                            clearInterval(statusInterval);
                        if (progressInterval)
                            clearInterval(progressInterval);
                        setIsTranslating(false);
                        const failedMessage = t("chatbot:modal.translation.error.failed");
                        setTranslationError({
                            errorType: CharacterFlow.GET_TRANSLATION,
                            errorMessage: failedMessage,
                            errorCode: "",
                        });
                        onError?.(failedMessage);
                    }
                }
                catch (error) {
                    if (statusInterval)
                        clearInterval(statusInterval);
                    if (progressInterval)
                        clearInterval(progressInterval);
                    setIsTranslating(false);
                    const apiError = error;
                    onError?.(apiError?.message || t("error:fallbackError"));
                }
            }, POLL_STATUS_INTERVAL_TIME); // Poll status every 7 seconds
        }
        catch (error) {
            const apiError = error;
            if (progressInterval) {
                clearInterval(progressInterval);
            }
            if (statusInterval) {
                clearInterval(statusInterval);
            }
            setIsTranslating(false);
            const errorMessage = apiError.message || t("error:fallbackError");
            onError?.(errorMessage);
            throw error;
        }
    }, [createTranslation, getTranslation, userChatLanguage, refreshCharacter, t]);
    return {
        isLoading: loading,
        loadingType,
        errorType: translationError?.errorType || null,
        errorMessage: translationError?.errorMessage || null,
        errorCode: translationError?.errorCode || null,
        onUserStartTranslation,
        resetError,
        translationProgress,
        translationStatus: localizedTranslationStatus,
        isTranslating,
        resetStatus,
    };
}
